import {
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { memo, useCallback } from 'react';

import { useLoadMoreSessions } from '@/components/SearchAssistant/hooks/useChatData';
import { searchAssistantStore } from '@/stores/searchAssistantStore';

const styles = {
  sessionList: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: 0,
    margin: 0,
  },
  sessionItem: {
    borderLeft: `4px solid transparent`,
    '&.Mui-selected': {
      backgroundColor: (theme) => theme.palette.action.selected,
      borderLeft: (theme) => `4px solid ${theme.palette.baseColors.fireOrange.c100}`,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.action.selected,
      },
    },
    '&:hover': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  },
  sessionListItem: {
    scrollSnapAlign: 'start',
  },
  sessionTitle: {
    fontWeight: 500,
    color: (theme) => theme.palette.baseColors.white.c100,
  },

  loadMoreButton: {
    margin: (theme) => theme.spacing(1),
    textTransform: 'none',
    color: (theme) => theme.palette.baseColors.fireOrange.c100,
    borderColor: (theme) => theme.palette.baseColors.fireOrange.c100,
    '&:hover': {
      backgroundColor: (theme) => theme.palette.baseColors.fireOrange.c10,
      borderColor: (theme) => theme.palette.baseColors.fireOrange.c100,
    },
  },
};

const LoadMoreButton = ({ sessions = [] }) => {
  const loadMore = useLoadMoreSessions();
  const hasMoreSessions = searchAssistantStore.use.hasMoreSessions();
  const loadingMoreSessions = searchAssistantStore.use.loadingMoreSessions();

  if (!hasMoreSessions || sessions.length === 0) {
    return null;
  }

  return (
    <ListItem
      sx={{
        ...styles.sessionListItem,
        marginTop: 'auto',
      }}
    >
      <Button
        variant="outlined"
        onClick={loadMore}
        disabled={loadingMoreSessions}
        fullWidth
        sx={styles.loadMoreButton}
      >
        {loadingMoreSessions ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading...
          </>
        ) : (
          'Load More'
        )}
      </Button>
    </ListItem>
  );
};

const ChatSideBarList = memo(() => {
  const selectSession = searchAssistantStore.use.selectSession();
  const currentSessionId = searchAssistantStore.use.currentSessionId();
  const sessions = searchAssistantStore.use.sessions();

  const handleSessionSelect = useCallback(
    (sessionId) => {
      selectSession(sessionId);
    },
    [selectSession],
  );

  const sessionsArray = Object.values(sessions).toSorted(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
  );

  return (
    <List sx={styles.sessionList}>
      {sessionsArray.map((session) => (
        <ListItem key={session.id} disablePadding sx={styles.sessionListItem}>
          <ListItemButton
            selected={session.id === currentSessionId}
            onClick={() => handleSessionSelect(session.id)}
            sx={styles.sessionItem}
          >
            <ListItemText
              primary={
                <Typography variant="body2" noWrap sx={styles.sessionTitle}>
                  {session.displayTitle}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
      {sessionsArray.length === 0 && (
        <ListItem sx={styles.sessionListItem}>
          <ListItemText
            primary={
              <Typography variant="body2" color="text.secondary" align="center">
                No chat sessions yet
              </Typography>
            }
          />
        </ListItem>
      )}
      <LoadMoreButton sessions={sessionsArray} />
    </List>
  );
});

export { ChatSideBarList };
